import { useMemo } from 'react';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import type { ComplianceStep } from '@/types/planData';
import { shouldSurfaceExceptionCard, type ExceptionConditions } from '@/utils/classificationEngine';

// ---------------------------------------------------------------------------
// ExceptionCard (blueprint v2 C-4) — ANSI/ASSP A10.48-2023 §4.4.4.1
// exception cards. Two exception paths:
//   (b) Lightweight hoisting with rigging attached away from primary structure
//   (a) Component modifications (connections of structural members)
// Each renders 4 condition checkboxes; NOTHING is pre-checked. Auto-surface
// rule for (b): gross load ≤ 200 lb AND attachment Δ ≤ 4 ft. Each check is
// stored with timestamp + QP identity ("maintained and documented within the
// construction plan"). Toggling condition iv inserts the §9.6 proof-test
// compliance step (via onComplianceStepsChange); unchecking removes it.
// Any unchecked/in-question condition ⇒ Class IV + QE banner.
// ---------------------------------------------------------------------------

export type ExceptionPath = '4.4.4.1(a)' | '4.4.4.1(b)';

export interface ConditionMeta {
  checkedAt: string | null;
  checkedBy: string | null;
}

export interface ExceptionCardProps {
  path: ExceptionPath;
  conditions: ExceptionConditions;
  conditionsMeta: Record<string, ConditionMeta>;
  qpName: string;
  /** Lift gross load / attachment delta drive the (b) auto-surface rule. */
  grossLoadLbs: number | null;
  attachmentDeltaFt: number | null;
  /** Force the card visible even when the auto-surface rule doesn't fire (e.g. IV(e) answered 'other'). */
  forceVisible?: boolean;
  onConditionsChange: (conditions: ExceptionConditions, meta: Record<string, ConditionMeta>) => void;
  complianceSteps: ComplianceStep[];
  onComplianceStepsChange: (steps: ComplianceStep[]) => void;
}

const PROOF_TEST_STEP_REF = 'A10.48-2023 §9.6';

const CONDITIONS_B: { key: keyof ExceptionConditions; label: string }[] = [
  {
    key: 'goodCondition',
    label:
      'i. All rigging attachments are made at locations in good condition and in a manner which avoids slippage and prevents damage to structural members and appurtenances.',
  },
  {
    key: 'within4ft',
    label:
      'ii. Primary or redundant lift system rigging is attached to the structure at the vertical tower leg(s) at panel points (latticed) or the main pole shaft (monopole), located no more than 4 feet vertically below the highest rigging attachment point (Ref. Appendix A-4(a)/A-4(b)).',
  },
  { key: 'max200lb', label: 'iii. The gross load does not exceed 200 lbs.' },
  {
    key: 'proofTest',
    label:
      'iv. A documented proof test in accordance with Section 9.6 of this standard is completed prior to active hoisting operations.',
  },
];

const CONDITIONS_A: { key: keyof ExceptionConditions; label: string }[] = [
  { key: 'goodCondition', label: 'i. At least one level of redundancy is always maintained.' },
  {
    key: 'within4ft',
    label: 'ii. The structural member remains secure and engaged in the bracing system.',
  },
  { key: 'max200lb', label: 'iii. The work is completed within a continuous workday.' },
  {
    key: 'proofTest',
    label:
      'iv. A documented proof test in accordance with Section 9.6 of this standard is completed prior to active hoisting operations.',
  },
];

/** Insert or remove the §9.6 proof-test compliance step (idempotent per path). */
function syncProofTestStep(
  steps: ComplianceStep[],
  path: ExceptionPath,
  inserted: boolean,
  qpName: string
): ComplianceStep[] {
  const stepId = `proof-test-${path.replace(/[^a-z0-9]/gi, '')}`;
  const existing = steps.findIndex((s) => s.ref === PROOF_TEST_STEP_REF && s.fields?.exceptionCardId === stepId);
  if (inserted) {
    const step: ComplianceStep = {
      ref: PROOF_TEST_STEP_REF,
      requirement:
        'Documented proof test per §9.6 completed prior to active hoisting operations — log it in the Proof Test Log below (125% of max anticipated lifted load, held ≥ 5 minutes, timestamped photo pair).',
      status: 'pending',
      by: qpName || undefined,
      fields: { exceptionCardId: stepId, exceptionPath: path },
    };
    if (existing >= 0) {
      const next = [...steps];
      next[existing] = step;
      return next;
    }
    return [...steps, step];
  }
  if (existing >= 0) {
    return steps.filter((_, i) => i !== existing);
  }
  return steps;
}

export function ExceptionCard({
  path,
  conditions,
  conditionsMeta,
  qpName,
  grossLoadLbs,
  attachmentDeltaFt,
  forceVisible = false,
  onConditionsChange,
  complianceSteps,
  onComplianceStepsChange,
}: ExceptionCardProps) {
  const conditionList = path === '4.4.4.1(b)' ? CONDITIONS_B : CONDITIONS_A;
  const autoSurface = useMemo(
    () => shouldSurfaceExceptionCard(grossLoadLbs, attachmentDeltaFt),
    [grossLoadLbs, attachmentDeltaFt]
  );
  const visible = path === '4.4.4.1(a)' ? forceVisible : autoSurface || forceVisible;
  const allChecked = conditionList.every(({ key }) => conditions[key]);

  if (!visible) return null;

  const toggle = (key: keyof ExceptionConditions, checked: boolean) => {
    const next: ExceptionConditions = { ...conditions, [key]: checked };
    const nextMeta: Record<string, ConditionMeta> = {
      ...conditionsMeta,
      [key]: {
        checkedAt: checked ? new Date().toISOString() : null,
        checkedBy: checked ? qpName || 'Qualified Person' : null,
      },
    };
    onConditionsChange(next, nextMeta);
    if (key === 'proofTest') {
      // AC: toggling condition iv inserts the §9.6 proof-test compliance step.
      onComplianceStepsChange(syncProofTestStep(complianceSteps, path, checked, qpName));
    }
  };

  return (
    <div
      className={`border rounded-lg p-4 ${allChecked ? 'border-green-300 bg-green-50' : 'border-amber-300 bg-amber-50'}`}
      data-testid={`exception-card-${path}`}
    >
      <div className="flex items-start gap-2">
        {allChecked ? (
          <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
        ) : (
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
        )}
        <div>
          <p className="text-sm font-semibold text-gray-900">
            §4.4.4.1{path.slice(-3)} {path === '4.4.4.1(b)' ? 'Lightweight Hoisting Exception' : 'Component Modification Exception'}
          </p>
          <p className="text-xs text-gray-600">
            {allChecked
              ? 'All four conditions maintained and documented — may be deemed Class III by the qualified person.'
              : 'You may qualify — confirm all 4 conditions. Any unchecked or in-question condition ⇒ Class IV and a qualified engineer shall be engaged per ANSI/TIA-322-A.'}
          </p>
        </div>
      </div>

      <div className="mt-3 space-y-2">
        {conditionList.map(({ key, label }) => (
          <label key={key} className="flex items-start gap-2 text-sm text-gray-800">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={conditions[key]}
              onChange={(e) => toggle(key, e.target.checked)}
            />
            <span>
              {label}
              {conditionsMeta[key]?.checkedAt && (
                <span className="block text-xs text-gray-500">
                  Checked {new Date(conditionsMeta[key].checkedAt as string).toLocaleString()} by{' '}
                  {conditionsMeta[key].checkedBy}
                </span>
              )}
            </span>
          </label>
        ))}
      </div>

      {!allChecked && (
        <p className="mt-3 text-xs font-medium text-red-700">
          Class IV unless all conditions are documented — engage Qualified Engineer per ANSI/TIA-322-A
          (A10.48-2023 §4.4.4.1).
        </p>
      )}
      {conditions.proofTest && (
        <p className="mt-2 text-xs text-blue-800">
          §9.6 proof-test compliance step added — document each test in the Proof Test Log (one record per
          rigging configuration + location).
        </p>
      )}
    </div>
  );
}
